import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import { adminJson } from "../utils/adminSession";
import AdminConfirmDialog from "../components/AdminConfirmDialog";
import { deliveryPublicPath, deliveryUrlPrefix } from "../components/deliveryHref";

const EMPTY_PRODUCT = { category: "", product: "", productName: "", nameEdited: false };
const EMPTY = {
  title: "",
  slug: "",
  products: [{ ...EMPTY_PRODUCT }],
  deliveryLocation: "",
  deliveryDate: "",
  images: [],
  description: "",
  seoTitle: "",
  seoDescription: "",
  isPublished: true,
};
const TITLE_MAX = 150;
const SLUG_MAX = 180;
const NAME_MAX = 150;
const LOCATION_MAX = 150;
const ALT_MAX = 160;
const DESC_MAX = 1000;
const SEO_TITLE_MAX = 60;
const SEO_DESC_MAX = 160;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const PAGE_SIZE = 5;
const PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const PREVIEW_HOST = "atindustrialsupply.com";
const inputClass =
  "w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-at-dark outline-none focus:border-at-gold";

function toSlug(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX);
}

function uniqueSlug(requested, deliveries, excludeId) {
  const base = toSlug(requested);
  if (!base) return "";
  const taken = new Set(
    (deliveries || [])
      .filter((item) => item?._id && item._id !== excludeId)
      .map((item) => String(item.slug || ""))
  );
  if (!taken.has(base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const suffix = `-${n}`;
    const candidate = `${base.slice(0, Math.max(1, SLUG_MAX - suffix.length))}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base.slice(0, Math.max(1, SLUG_MAX - 12))}-${Date.now()}`;
}

function isPublished(item) {
  return item?.isPublished !== false;
}

function idOf(value) {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
}

function catalogCategoryId(item) {
  return idOf(item?.category);
}

function productIdsOf(item) {
  const list = Array.isArray(item?.product) ? item.product : item?.product ? [item.product] : [];
  return list
    .map((row) => {
      if (row && typeof row === "object") return idOf(row.product) || idOf(row);
      return String(row || "");
    })
    .filter(Boolean);
}

function productNamesOf(item) {
  const list = Array.isArray(item?.product) ? item.product : [];
  const nested = list
    .map((row) => (row && typeof row === "object" ? String(row.productName || "").trim() : ""))
    .filter(Boolean);
  if (nested.length) return nested;
  const raw = item?.productName;
  const fallback = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return fallback.map((name) => String(name || "").trim()).filter(Boolean);
}

function productNamesLabel(item) {
  const names = productNamesOf(item);
  return names.length ? names.join(", ") : "—";
}

function rowsFromDelivery(item) {
  const list = Array.isArray(item?.product) ? item.product : item?.product ? [item.product] : [];
  if (list.length === 0) return [{ ...EMPTY_PRODUCT }];
  return list.map((row) => ({
    category: row && typeof row === "object" ? idOf(row.category) : "",
    product: row && typeof row === "object" ? idOf(row.product) || idOf(row) : idOf(row),
    productName: row && typeof row === "object" ? String(row.productName || "").trim() : "",
    nameEdited: true,
  }));
}

function firstImage(item) {
  const images = Array.isArray(item?.images) ? item.images : [];
  return images[0] || null;
}

function toInputDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDeliveryDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function dateForSave(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const date = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

function AdminDeliveriesPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const slugEdited = useRef(false);
  const [deliveries, setDeliveries] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [productFilter, setProductFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [view, setView] = useState("list");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deliveries.filter((item) => {
      const hay = [item.title, item.slug, productNamesLabel(item), item.deliveryLocation]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");
      const searchMatch = !q || hay.includes(q);
      const productMatch = productFilter === "all" || productIdsOf(item).includes(productFilter);
      const published = isPublished(item);
      const statusMatch =
        statusFilter === "all" ||
        (statusFilter === "published" && published) ||
        (statusFilter === "draft" && !published);
      const key = toInputDate(item.deliveryDate);
      const fromMatch = !dateFrom || key >= dateFrom;
      const toMatch = !dateTo || key <= dateTo;
      return searchMatch && productMatch && statusMatch && fromMatch && toMatch;
    });
  }, [deliveries, search, productFilter, statusFilter, dateFrom, dateTo]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [deliveryList, productList, categoryList] = await Promise.all([
          fetch(`${getApiBaseUrl()}/deliveries`, { headers: { Accept: "application/json" } }).then((res) => {
            if (!res.ok) throw new Error("Unable to load deliveries");
            return res.json();
          }),
          fetch(`${getApiBaseUrl()}/products`, { headers: { Accept: "application/json" } }).then((res) => {
            if (!res.ok) throw new Error("Unable to load products");
            return res.json();
          }),
          fetch(`${getApiBaseUrl()}/categories`, { headers: { Accept: "application/json" } }).then((res) => {
            if (!res.ok) throw new Error("Unable to load categories");
            return res.json();
          }),
        ]);
        if (!cancelled) {
          setDeliveries(Array.isArray(deliveryList) ? deliveryList : []);
          setProducts(Array.isArray(productList) ? productList : []);
          setCategories(Array.isArray(categoryList) ? categoryList : []);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Unable to load deliveries");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, productFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [panelOpen]);

  function onUnauthorized(err) {
    if (err?.status === 401) {
      navigate("/login", { replace: true });
      return true;
    }
    return false;
  }

  function openCreate() {
    slugEdited.current = false;
    setEditingId(null);
    setForm({ ...EMPTY, products: [{ ...EMPTY_PRODUCT }] });
    setError("");
    setNotice("");
    setPanelOpen(true);
  }

  function openEdit(item) {
    slugEdited.current = true;
    setEditingId(item._id);
    setForm({
      title: item.title || "",
      slug: item.slug || "",
      products: rowsFromDelivery(item),
      deliveryLocation: item.deliveryLocation || "",
      deliveryDate: toInputDate(item.deliveryDate),
      images: Array.isArray(item.images)
        ? item.images.map((image) => ({
            url: image.url || "",
            altText: image.altText || "",
          }))
        : [],
      description: item.description || "",
      seoTitle: item.seoTitle || "",
      seoDescription: item.seoDescription || "",
      isPublished: isPublished(item),
    });
    setError("");
    setNotice("");
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditingId(null);
    setForm({ ...EMPTY, products: [{ ...EMPTY_PRODUCT }] });
    slugEdited.current = false;
  }

  function onTitleChange(value) {
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: slugEdited.current ? prev.slug : uniqueSlug(toSlug(value), deliveries, editingId),
    }));
  }

  function onCategoryChange(index, categoryId) {
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((row, i) => {
        if (i !== index) return row;
        const stillValid =
          row.product && catalogCategoryId(products.find((item) => item._id === row.product)) === categoryId;
        return {
          ...row,
          category: categoryId,
          product: stillValid ? row.product : "",
          productName: stillValid ? row.productName : "",
          nameEdited: stillValid ? row.nameEdited : false,
        };
      }),
    }));
  }

  function onProductChange(index, id) {
    const catalog = products.find((item) => item._id === id);
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((row, i) => {
        if (i !== index) return row;
        return {
          ...row,
          category: row.category || catalogCategoryId(catalog),
          product: id,
          productName: row.nameEdited ? row.productName : catalog?.productName || "",
        };
      }),
    }));
  }

  function onProductNameChange(index, value) {
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((row, i) =>
        i === index ? { ...row, productName: value, nameEdited: true } : row
      ),
    }));
  }

  function addProductRow() {
    setForm((prev) => ({
      ...prev,
      products: [...prev.products, { ...EMPTY_PRODUCT }],
    }));
  }

  function removeProductRow(index) {
    setForm((prev) => {
      if (prev.products.length <= 1) return prev;
      return { ...prev, products: prev.products.filter((_, i) => i !== index) };
    });
  }

  async function onPickFiles(fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = [];
      for (const file of files) {
        if (!PHOTO_TYPES.includes(file.type)) {
          throw new Error("Delivery photos must be JPEG, PNG or WebP files");
        }
        if (file.size > PHOTO_MAX_BYTES) {
          throw new Error("Each delivery photo must be 5 MB or smaller");
        }
        const data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ""));
          reader.onerror = () => reject(new Error("Unable to read file"));
          reader.readAsDataURL(file);
        });
        const result = await adminJson("/media", {
          method: "POST",
          body: {
            purpose: "deliveries",
            filename: file.name,
            contentType: file.type,
            data,
          },
        });
        uploaded.push({ url: result.path, altText: "" });
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...uploaded] }));
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to upload photos");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function removeImage(index) {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  }

  function setImageAlt(index, altText) {
    setForm((prev) => ({
      ...prev,
      images: prev.images.map((image, i) => (i === index ? { ...image, altText } : image)),
    }));
  }

  async function onSave(publish) {
    const title = form.title.trim();
    const slug = uniqueSlug(form.slug.trim(), deliveries, editingId);
    const productRows = (form.products || []).map((row) => ({
      category: String(row.category || "").trim(),
      product: String(row.product || "").trim(),
      productName: String(row.productName || "").trim(),
    }));
    const deliveryLocation = form.deliveryLocation.trim();
    const description = form.description.trim();
    const images = form.images
      .map((image) => ({
        url: String(image.url || "").trim(),
        altText: String(image.altText || "").trim(),
      }))
      .filter((image) => image.url);
    if (!title) {
      setError("Delivery title is required");
      return;
    }
    if (title.length > TITLE_MAX) {
      setError(`Delivery title must be ${TITLE_MAX} characters or fewer`);
      return;
    }
    if (!slug || !SLUG_RE.test(slug)) {
      setError("Slug can only contain lowercase letters, numbers and hyphens");
      return;
    }
    if (productRows.length === 0 || productRows.some((row) => !row.category || !row.product)) {
      setError("Each product needs a category and product");
      return;
    }
    if (productRows.some((row) => !row.productName)) {
      setError("Each product needs a product name");
      return;
    }
    if (!deliveryLocation) {
      setError("Delivery location is required");
      return;
    }
    if (!form.deliveryDate) {
      setError("Delivery date is required");
      return;
    }
    if (images.length === 0) {
      setError("At least one delivery photo is required");
      return;
    }
    if (images.some((image) => !image.altText)) {
      setError("Each delivery photo needs alt text");
      return;
    }
    if (!description) {
      setError("Description is required");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        title,
        slug,
        product: productRows,
        deliveryLocation,
        deliveryDate: dateForSave(form.deliveryDate),
        images,
        description,
        seoTitle: form.seoTitle.trim(),
        seoDescription: form.seoDescription.trim(),
        isPublished: Boolean(publish),
      };
      if (editingId) {
        const saved = await adminJson(`/deliveries/${editingId}`, { method: "PUT", body: payload });
        setDeliveries((prev) => prev.map((item) => (item._id === saved._id ? saved : item)));
        setNotice("Delivery updated.");
      } else {
        const created = await adminJson("/deliveries", { method: "POST", body: payload });
        setDeliveries((prev) => [created, ...prev]);
        setNotice("Delivery created.");
      }
      closePanel();
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to save delivery");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(item) {
    setDeleteTarget(item);
  }

  async function confirmDelete() {
    const item = deleteTarget;
    if (!item) return;
    setError("");
    setNotice("");
    setSaving(true);
    try {
      await adminJson(`/deliveries/${item._id}`, { method: "DELETE" });
      setDeliveries((prev) => prev.filter((row) => row._id !== item._id));
      setNotice("Delivery deleted.");
      setDeleteTarget(null);
      if (editingId === item._id) closePanel();
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to delete delivery");
    } finally {
      setSaving(false);
    }
  }

  const from = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(filtered.length, currentPage * PAGE_SIZE);
  const slugPrefix = deliveryUrlPrefix(form.deliveryDate);
  const previewTitle = form.seoTitle.trim() || form.title.trim() || "Delivery title";
  const previewUrl = `${PREVIEW_HOST}${slugPrefix}${form.slug.trim() || "slug"}`;
  const previewDesc =
    form.seoDescription.trim() || form.description.trim() || "Briefly describe what was delivered and its intended application.";

  function StatusBadge({ item }) {
    if (isPublished(item)) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Published
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">
        <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
        Draft
      </span>
    );
  }

  function RowActions({ item }) {
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => openEdit(item)}
          className="rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-at-dark"
          aria-label={`Edit ${item.title}`}
        >
          <PencilIcon />
        </button>
        <button
          type="button"
          onClick={() => onDelete(item)}
          className="rounded-md p-2 text-neutral-500 hover:bg-red-50 hover:text-red-600"
          aria-label={`Delete ${item.title}`}
        >
          <TrashIcon />
        </button>
      </div>
    );
  }

  return (
    <div className="-mt-2">
      <p className="text-xs text-neutral-400">Dashboard / Deliveries</p>
      <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-at-dark">Deliveries</h1>
          <p className="mt-1 text-sm text-neutral-500">Record and publish completed product deliveries.</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
        >
          + Add Delivery
        </button>
      </div>

      {error && !panelOpen && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {notice && <p className="mt-4 text-sm text-emerald-700">{notice}</p>}

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-sm font-medium text-at-dark">
          {loading ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "Delivery" : "Deliveries"}`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search product or location..."
            className={`${inputClass} max-w-xs`}
          />
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className={`${inputClass} w-auto`}
            aria-label="Filter by product"
          >
            <option value="all">All Products</option>
            {products.map((item) => (
              <option key={item._id} value={item._id}>
                {item.productName}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`${inputClass} w-auto`}
            aria-label="Filter by status"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={`${inputClass} w-auto`}
            aria-label="From date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={`${inputClass} w-auto`}
            aria-label="To date"
          />
          <div className="flex overflow-hidden rounded-md border border-neutral-200 bg-white">
            <button
              type="button"
              onClick={() => setView("list")}
              className={`px-2.5 py-2 ${view === "list" ? "bg-at-gold text-at-dark" : "text-neutral-400 hover:text-at-dark"}`}
              aria-label="List view"
              aria-pressed={view === "list"}
            >
              <ListIcon />
            </button>
            <button
              type="button"
              onClick={() => setView("grid")}
              className={`px-2.5 py-2 ${view === "grid" ? "bg-at-gold text-at-dark" : "text-neutral-400 hover:text-at-dark"}`}
              aria-label="Grid view"
              aria-pressed={view === "grid"}
            >
              <GridIcon />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-neutral-500">Loading deliveries…</p>
      ) : filtered.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-neutral-300 bg-white px-6 py-16 text-center">
          <p className="text-base font-semibold text-at-dark">No deliveries yet</p>
          <p className="mt-1 text-sm text-neutral-500">Record a completed product delivery.</p>
          <button
            type="button"
            onClick={openCreate}
            className="mt-5 rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
          >
            + Add Delivery
          </button>
        </div>
      ) : view === "list" ? (
        <div className="mt-5 overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-neutral-100 text-xs font-medium uppercase tracking-wide text-neutral-400">
              <tr>
                <th className="px-4 py-3 font-medium">Delivery</th>
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Destination</th>
                <th className="px-4 py-3 font-medium">Delivery Date</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((item) => {
                const photo = firstImage(item);
                const src = publicMediaSrc(photo?.url);
                return (
                  <tr key={item._id} className="border-b border-neutral-100 last:border-b-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-16 shrink-0 overflow-hidden rounded bg-neutral-100">
                          {src ? <img src={src} alt={photo?.altText || ""} className="h-full w-full object-cover" /> : null}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-at-dark">{item.title}</p>
                          {item.slug ? <p className="text-xs text-neutral-400">{deliveryPublicPath(item)}</p> : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{productNamesLabel(item)}</td>
                    <td className="px-4 py-3 text-neutral-600">{item.deliveryLocation || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-neutral-500">{formatDeliveryDate(item.deliveryDate)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge item={item} />
                    </td>
                    <td className="px-4 py-3">
                      <RowActions item={item} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {paged.map((item) => {
            const photo = firstImage(item);
            const src = publicMediaSrc(photo?.url);
            return (
              <article key={item._id} className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
                <div className="h-40 bg-neutral-100">
                  {src ? <img src={src} alt={photo?.altText || ""} className="h-full w-full object-cover" /> : null}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-at-dark">{item.title}</p>
                      <p className="mt-1 text-sm text-neutral-500">{productNamesLabel(item)}</p>
                      <p className="text-sm text-neutral-500">{item.deliveryLocation || "—"}</p>
                      <p className="mt-1 text-xs text-neutral-400">{formatDeliveryDate(item.deliveryDate)}</p>
                    </div>
                    <StatusBadge item={item} />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <RowActions item={item} />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="mt-6 flex flex-col items-center justify-between gap-3 text-sm text-neutral-500 sm:flex-row">
          <p>
            Showing {from}–{to} of {filtered.length} deliveries
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border border-neutral-200 bg-white px-2 py-1.5 disabled:opacity-40"
              aria-label="Previous page"
            >
              ‹
            </button>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                className={`min-w-[2rem] rounded-md px-2 py-1.5 ${
                  n === currentPage ? "bg-at-gold text-at-dark" : "border border-neutral-200 bg-white"
                }`}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              className="rounded-md border border-neutral-200 bg-white px-2 py-1.5 disabled:opacity-40"
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        </div>
      )}

      {panelOpen && (
        <div className="fixed inset-0 z-50 flex h-[100dvh] max-h-[100dvh] justify-end overflow-hidden bg-black/30">
          <button type="button" aria-label="Close panel" className="hidden h-full flex-1 sm:block" onClick={closePanel} />
          <aside className="flex h-full min-h-0 w-full max-w-xl flex-col overflow-hidden bg-white shadow-xl">
            <div className="flex shrink-0 items-start justify-between border-b border-neutral-100 px-6 py-5">
              <div>
                <h2 className="text-xl font-semibold text-at-dark">{editingId ? "Edit Delivery" : "Add Delivery"}</h2>
                <p className="mt-1 text-sm text-neutral-500">
                  {editingId ? "Update this completed product delivery." : "Record a completed product delivery."}
                </p>
              </div>
              <button type="button" onClick={closePanel} className="text-2xl leading-none text-neutral-400 hover:text-at-dark" aria-label="Close">
                ×
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">
              {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

              <h3 className="text-sm font-semibold text-at-dark">Delivery Information</h3>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="title">
                    Delivery Title *
                  </label>
                  <input
                    id="title"
                    value={form.title}
                    maxLength={TITLE_MAX}
                    onChange={(e) => onTitleChange(e.target.value)}
                    placeholder="e.g. SS400 Flat Bars Delivered to Johor Bahru"
                    className={`${inputClass} mt-1.5`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="slug">
                    URL Slug *
                  </label>
                  <div className="mt-1.5 flex overflow-hidden rounded-md border border-neutral-200 focus-within:border-at-gold">
                    <span className="shrink-0 bg-neutral-100 px-2 py-2 text-xs text-neutral-500">
                      {slugPrefix}
                    </span>
                    <input
                      id="slug"
                      value={form.slug}
                      maxLength={SLUG_MAX}
                      onChange={(e) => {
                        slugEdited.current = true;
                        setForm((prev) => ({ ...prev, slug: e.target.value.toLowerCase() }));
                      }}
                      placeholder="ss400-flat-bars-johor-bahru"
                      className="w-full bg-white px-2 py-2 text-sm text-at-dark outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-at-dark">Products *</p>
                  <button
                    type="button"
                    onClick={addProductRow}
                    className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-at-dark hover:bg-neutral-50"
                  >
                    + Add product
                  </button>
                </div>
                <p className="mt-1 text-xs text-neutral-400">Choose a category first, then the delivered product. The name is saved as a snapshot.</p>
                {(form.products || []).map((row, index) => {
                  const options = row.category
                    ? products.filter((item) => catalogCategoryId(item) === row.category)
                    : [];
                  return (
                    <div key={index} className="mt-3 rounded-lg border border-neutral-200 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-neutral-400">Product {index + 1}</p>
                        {form.products.length > 1 ? (
                          <button
                            type="button"
                            onClick={() => removeProductRow(index)}
                            className="text-xs text-neutral-500 hover:text-red-600"
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                      <div className="mt-2 grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-at-dark" htmlFor={`category-${index}`}>
                            Category *
                          </label>
                          <select
                            id={`category-${index}`}
                            value={row.category}
                            onChange={(e) => onCategoryChange(index, e.target.value)}
                            className={`${inputClass} mt-1.5`}
                          >
                            <option value="">Select a category</option>
                            {categories.map((item) => (
                              <option key={item._id} value={item._id}>
                                {item.categoryName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-at-dark" htmlFor={`product-${index}`}>
                            Product *
                          </label>
                          <select
                            id={`product-${index}`}
                            value={row.product}
                            onChange={(e) => onProductChange(index, e.target.value)}
                            className={`${inputClass} mt-1.5`}
                            disabled={!row.category}
                          >
                            <option value="">{row.category ? "Select a product" : "Select a category first"}</option>
                            {options.map((item) => (
                              <option key={item._id} value={item._id}>
                                {item.productName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-at-dark" htmlFor={`productName-${index}`}>
                            Product Name *
                          </label>
                          <input
                            id={`productName-${index}`}
                            value={row.productName}
                            maxLength={NAME_MAX}
                            onChange={(e) => onProductNameChange(index, e.target.value)}
                            placeholder="Saved as a snapshot of the delivered product"
                            className={`${inputClass} mt-1.5`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="deliveryLocation">
                    Delivery Location *
                  </label>
                  <input
                    id="deliveryLocation"
                    value={form.deliveryLocation}
                    maxLength={LOCATION_MAX}
                    onChange={(e) => setForm((prev) => ({ ...prev, deliveryLocation: e.target.value }))}
                    placeholder="City, State"
                    className={`${inputClass} mt-1.5`}
                  />
                  <p className="mt-1 text-xs text-neutral-400">Use a general location only—never a customer’s full address.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="deliveryDate">
                    Delivery Date *
                  </label>
                  <input
                    id="deliveryDate"
                    type="date"
                    value={form.deliveryDate}
                    onChange={(e) => setForm((prev) => ({ ...prev, deliveryDate: e.target.value }))}
                    className={`${inputClass} mt-1.5`}
                  />
                </div>
              </div>

              <p className="mt-6 text-sm font-medium text-at-dark">Delivery Photos *</p>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  onPickFiles(e.dataTransfer.files);
                }}
                className={`mt-1.5 rounded-lg border-2 border-dashed px-4 py-8 text-center ${
                  dragOver ? "border-at-gold bg-amber-50" : "border-neutral-200 bg-neutral-50"
                }`}
              >
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="text-sm font-medium text-at-dark underline disabled:opacity-60"
                >
                  {uploading ? "Uploading…" : "Upload delivery photos"}
                </button>
                <p className="mt-2 text-xs text-neutral-400">JPG, PNG or WebP, maximum 5 MB each</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={(e) => onPickFiles(e.target.files)}
                />
              </div>

              {form.images.length > 0 && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {form.images.map((image, index) => (
                    <div key={`${image.url}-${index}`} className="rounded-lg border border-neutral-200 p-2">
                      <div className="relative h-24 overflow-hidden rounded bg-neutral-100">
                        {publicMediaSrc(image.url) ? (
                          <img src={publicMediaSrc(image.url)} alt={image.altText || ""} className="h-full w-full object-cover" />
                        ) : null}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute right-1 top-1 rounded bg-white/90 px-1.5 text-xs text-neutral-600 hover:text-red-600"
                          aria-label="Remove photo"
                        >
                          ×
                        </button>
                      </div>
                      <label className="mt-2 block text-xs font-medium text-at-dark" htmlFor={`alt-${index}`}>
                        Image Alt Text *
                      </label>
                      <input
                        id={`alt-${index}`}
                        value={image.altText}
                        maxLength={ALT_MAX}
                        onChange={(e) => setImageAlt(index, e.target.value)}
                        placeholder="e.g. Steel bars loaded for delivery"
                        className={`${inputClass} mt-1`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <label className="mt-6 block text-sm font-medium text-at-dark" htmlFor="description">
                Description *
              </label>
              <textarea
                id="description"
                rows={4}
                value={form.description}
                maxLength={DESC_MAX}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Briefly describe what was delivered and its intended application."
                className={`${inputClass} mt-1.5 resize-y`}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">
                {form.description.length} / {DESC_MAX}
              </p>

              <h3 className="mt-8 text-sm font-semibold text-at-dark">SEO Settings</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="seoTitle">
                    SEO Title
                  </label>
                  <input
                    id="seoTitle"
                    value={form.seoTitle}
                    maxLength={SEO_TITLE_MAX}
                    onChange={(e) => setForm((prev) => ({ ...prev, seoTitle: e.target.value }))}
                    placeholder="Enter SEO title..."
                    className={`${inputClass} mt-1.5`}
                  />
                  <p className="mt-1 text-right text-xs text-neutral-400">
                    {form.seoTitle.length} / {SEO_TITLE_MAX}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="seoDescription">
                    SEO Description
                  </label>
                  <textarea
                    id="seoDescription"
                    rows={3}
                    value={form.seoDescription}
                    maxLength={SEO_DESC_MAX}
                    onChange={(e) => setForm((prev) => ({ ...prev, seoDescription: e.target.value }))}
                    placeholder="Enter SEO description..."
                    className={`${inputClass} mt-1.5 resize-y`}
                  />
                  <p className="mt-1 text-right text-xs text-neutral-400">
                    {form.seoDescription.length} / {SEO_DESC_MAX}
                  </p>
                </div>
              </div>

              <p className="mt-4 text-sm font-medium text-at-dark">Google Search Preview</p>
              <div className="mt-1.5 rounded-md border border-neutral-200 bg-white px-3 py-3">
                <p className="text-sm text-[#1a0dab]">{previewTitle}</p>
                <p className="truncate text-xs text-emerald-700">{previewUrl}</p>
                <p className="mt-1 text-xs leading-snug text-neutral-500">{previewDesc}</p>
              </div>
            </div>

            <div className="shrink-0 border-t border-neutral-100 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-at-dark">Publishing</span>
                <label className="flex items-center gap-2 text-sm text-neutral-600">
                  <span className={`relative h-5 w-9 rounded-full ${form.isPublished ? "bg-at-gold" : "bg-neutral-300"}`}>
                    <input
                      type="checkbox"
                      className="peer absolute inset-0 cursor-pointer opacity-0"
                      checked={Boolean(form.isPublished)}
                      onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))}
                    />
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${
                        form.isPublished ? "left-4" : "left-0.5"
                      }`}
                    />
                  </span>
                  Publish delivery
                </label>
              </div>
              <p className="mb-3 text-xs text-neutral-400">Published deliveries appear in the public delivery gallery.</p>
              <div className="grid grid-cols-2 gap-2 sm:flex">
                <button
                  type="button"
                  onClick={closePanel}
                  className="rounded-md border border-neutral-300 px-3 py-2.5 text-sm font-medium text-at-dark"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={saving || uploading}
                  onClick={() => onSave(false)}
                  className="rounded-md border border-neutral-300 px-3 py-2.5 text-sm font-medium text-at-dark disabled:opacity-60 sm:flex-1"
                >
                  Save as Draft
                </button>
                <button
                  type="button"
                  disabled={saving || uploading}
                  onClick={() => onSave(true)}
                  className="col-span-2 rounded-md bg-at-gold px-3 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:opacity-60 sm:col-auto sm:flex-1"
                >
                  {saving ? "Saving…" : editingId ? "Save Changes" : "Create Delivery"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
      {deleteTarget ? (
        <AdminConfirmDialog
          title="Delete delivery?"
          message={`Delete “${deleteTarget.title}”? You can restore it from Recent Activities for 30 days.`}
          busy={saving}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </div>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 7h16" />
      <path d="M9 7V5h6v2" />
      <path d="M7 7l1 13h8l1-13" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3 6h.01" />
      <path d="M3 12h.01" />
      <path d="M3 18h.01" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

export default AdminDeliveriesPage;
